// @file Surface ai chat store

import { trackEvent } from '@@/bits/analytics'
import environment from '@@/bits/environment'
import { EventSourceWithHeaders } from '@@/bits/event_source_with_headers'
import { __ } from '@@/bits/intl'
import { asciiSafeStringify } from '@@/bits/json_stringify'
import { useSurfaceStore } from '@@/pinia/surface'
import { useSurfaceOnboardingPanelStore } from '@@/pinia/surface_onboarding_panel'
import { useSurfaceStartingStateStore } from '@@/pinia/surface_starting_state'
import PadletApi from '@@/surface/padlet_api'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { useSurfaceCurrentUserStore } from '@@/pinia/surface_current_user'

export enum Role {
  USER = 'user',
  ASSISTANT = 'assistant',
  SYSTEM = 'system',
}

export interface Message {
  role: string
  content: string
}

export interface Assistant {
  id: string
  name: string
  instructions: string
  model: string
}

export interface SerializedWall {
  title: string
  subtitle: string
  sections: Array<{
    section_id: string
    section_title: string
  }>
  posts: Array<{
    post_id: string
    section_id: string
    subject: string
    body: string
  }>
}

export const useSurfaceAIChatStore = defineStore('surfaceAIChatStore', () => {
  const DEFAULT_SUGGESTIONS = [
    __('What can you help me with?'),
    __('Add posts about a topic of my choosing.'),
    __('How do I add custom fields to my posts?'),
  ]

  const surfaceOnboardingPanelStore = useSurfaceOnboardingPanelStore()
  const surfaceStore = useSurfaceStore()
  const surfaceStartingStateStore = useSurfaceStartingStateStore()
  const surfaceCurrentUserStore = useSurfaceCurrentUserStore()
  const xSurfaceAIChatPanel = ref(false)
  const assistants = ref<Assistant[]>([])
  const currentAssistant = ref<Assistant | null>(null)
  const messages = ref<Message[]>([])
  const threadId = ref<string | null>(null)
  const source = ref<EventSourceWithHeaders | null>(null)

  const baseUrl = environment === 'development' ? 'https://morpheus.padlet.dev' : 'https://morpheus.padlet.com'

  const isEventStreamOpen = computed(() => source.value !== null)
  const hasMessages = computed(() => messages.value.length > 0)
  const isLoadingResponse = ref(false)

  const hasFinishedResponse = computed(() => {
    return !isEventStreamOpen.value && !isLoadingResponse.value
  })

  async function initializeAssistants(): Promise<void> {
    assistants.value = await PadletApi.SurfaceAIChat.getAssistants()

    if (assistants.value.length > 0) {
      currentAssistant.value = assistants.value[0]
    }
  }

  async function initializeEventSource(assistantId: string, wallId: number, threadId: string): Promise<void> {
    source.value = new EventSourceWithHeaders(
      `${baseUrl}/api/v1/assistant/${assistantId}/${wallId}/threads/${threadId}/event-stream`,
      {
        Authorization: `Bearer ${useSurfaceStartingStateStore().morpheusToken}`,
      },
    )
    source.value.addEventListener('message', (event) => {
      const data = JSON.parse(event.data)
      if (data.event === 'thread.message.created') {
        isLoadingResponse.value = false
      }
      if (data.event === 'thread.message.delta') {
        const messageDelta = data.data.delta.content[0].text.value
        addAssistantMessageDelta(messageDelta)
      }
      if (data.event === 'thread.message.completed') {
        // TODO: Figure out how to leave empty line in div. Chaining \n doesn't work with markdown-it
        addAssistantMessageDelta('\n\n')
      }
      if (
        data.event === 'thread.run.completed' ||
        data.event === 'thread.run.failed' ||
        data.event === 'thread.run.expired' ||
        data.event === 'thread.run.cancelled'
      ) {
        closeEventSource()
      }
    })
    source.value.addEventListener('error', (event) => {
      closeEventSource()
    })
    await source.value.connect()
  }

  function closeEventSource(): void {
    if (source.value != null) {
      source.value.close()
      source.value = null
    }
  }

  async function onMessage(message: Message): Promise<void> {
    pushUserMessage(message.content)
    trackEvent('SurfaceAiChat', 'UserMessage', message.content, message.content.length, {
      message_length: message.content.length,
      wall_id: surfaceStore.wallId,
      thread_id: threadId.value,
      message_history: messages.value,
    })

    pushAssistantMessage('')
    isLoadingResponse.value = true
    // Create thread and send pre message.
    if (threadId.value === null) {
      const response = await PadletApi.SurfaceAIChat.getMessageThread(surfaceStore.wallId)
      threadId.value = response.thread_id
      await PadletApi.SurfaceAIChat.sendMessage(threadId.value as string, {
        role: Role.SYSTEM,
        content: constructPreMessage(),
      })
    }
    // Send message to assistant
    // Need to await here to ensure message is inside thread
    const serializedWallResponse = await PadletApi.SurfaceAIChat.getSerializedWall(surfaceStore.wallId)
    const serializedWall: SerializedWall = serializedWallResponse.data.attributes
    message.content = constructFullMessage(message.content, serializedWall)
    await PadletApi.SurfaceAIChat.sendMessage(threadId.value as string, message)
    if (currentAssistant.value !== null && threadId.value !== null) {
      await initializeEventSource(currentAssistant.value.id, surfaceStore.wallId, threadId.value)
    }
  }

  function constructPreMessage(): string {
    return `# user context #
        Preferred language: ${surfaceCurrentUserStore.currentUser?.lang}
        Current location: ${surfaceStartingStateStore.currentCountry}
        Account type: ${surfaceCurrentUserStore.currentUser?.account_type}`
  }

  function constructFullMessage(message: string, serializedWall: SerializedWall): string {
    return `
        # padlet contents #
        ${asciiSafeStringify(serializedWall)}

        # user message #
        ${message}
    `
  }

  function resetMessages(): void {
    threadId.value = null
    messages.value = []
  }

  function addAssistantMessageDelta(delta: string): void {
    messages.value[messages.value.length - 1].content += delta
  }

  function pushAssistantMessage(content: string): void {
    messages.value.push({
      role: Role.ASSISTANT,
      content,
    })
  }

  function pushUserMessage(content: string): void {
    messages.value.push({
      role: Role.USER,
      content,
    })
  }

  function setCurrentAssistant(assistantId: string): void {
    currentAssistant.value = assistants.value?.find((assistant) => assistant.id === assistantId) ?? null
  }

  function toggleChatPanel(): void {
    if (surfaceOnboardingPanelStore.showOnboardingPanel) {
      surfaceOnboardingPanelStore.closeOnboardingPanel()
    }
    xSurfaceAIChatPanel.value = !xSurfaceAIChatPanel.value
  }

  return {
    DEFAULT_SUGGESTIONS,
    currentAssistant,
    assistants,
    messages,
    threadId,
    hasMessages,
    onMessage,
    resetMessages,
    toggleChatPanel,
    setCurrentAssistant,
    xSurfaceAIChatPanel,
    isEventStreamOpen,
    isLoadingResponse,
    hasFinishedResponse,
    initializeAssistants,
    initializeEventSource,
    closeEventSource,
  }
})
